import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { queriesEnabledAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import TenantAPI from "../../api/TenantAPI";

/*
    Expected subQuery structure 
    [
        {
            QueryField: "MultiSubitemAPIName",
            Query: {
                Standard Query Structure
                ex: https://emerge360.atlassian.net/wiki/spaces/GC2/pages/483196929/GrowthCloud+CRM+Search+API+Syntax
            }
        }
    ]

    parentRecord - The record that stores the MultiSubitems.
 */

/**
 * useQueriedSubitems - Hook for adding a query to the useRecord hook key - For querying MultiSubitems.
 * @param {string} moduleAPIName - The moduleAPIName for the parent record
 * @param {string} recordID - The record id for the parent record
 * @param {Array} subQueries - The sub queries for querying MultiSubitems on this record.
 * @param {boolean} disabled - For disabling the query for reasons not already included in the "enabled" option on the useQuery hook.
 */
const useQueriedSubitems = ({
  moduleAPIName,
  recordID,
  subQueries,
  disabled = false,
}: {
  moduleAPIName: string;
  recordID: string;
  subQueries: any;
  disabled?: boolean;
}) => {
  const queriesEnabled = useAtomValue(queriesEnabledAtom);
  const tenantID = useAtomValue(tenantIDAtom);

  const query = {
    Fields: ["*"],
    Filters: [
      {
        Group: false,
        Field: "_id",
        Method: "equals",
        Value: recordID,
      },
    ],
    SubQueries: subQueries,
  };

  const record = useQuery({
    queryKey: [tenantID, "record", moduleAPIName, recordID, query],
    queryFn: () =>
      TenantAPI.searchRecords({
        moduleAPIName,
        query,
      }).then((res) => {
        const { count, data, pagination } = res.data.data;

        return { record: data?.[0] ?? null, count, pagination };
      }),
    enabled:
      !!moduleAPIName &&
      !!recordID &&
      !!subQueries &&
      queriesEnabled &&
      !disabled,
  });

  return {
    record: record.data?.record ?? null,
    recordLoading: record.isLoading || queriesEnabled === false,
  };
};

export default useQueriedSubitems;
